import { inject, Injectable } from '@angular/core'
import { Capacitor } from '@capacitor/core'
import { Directory, Filesystem } from '@capacitor/filesystem'
import { Share } from '@capacitor/share'
import { FileOpener } from '@capawesome-team/capacitor-file-opener'
import { ToastController } from '@ionic/angular/standalone'
import { constructNow, endOfDay, format, startOfDay, startOfYesterday, subDays, subMonths } from 'date-fns'
import { BehaviorSubject } from 'rxjs'

import { HOME_SPORT_GROUPS } from '../../pages/home/constants/home-sport-groups.constant'
import { Bet } from '../../pages/my-bets/types/my-bets.type'
import { PROFIT_AND_LOSS_DATE_RANGES } from '../../pages/profit-and-loss/types/profit-and-loss.type'
import { SHORT_NUMBER_SUFFIXES, SUPPORTED_LOCALES, TABLE_SCROLL_CONSTANT } from '../constants/common.constant'
import { BetSlipSetting, UserData } from '../types/auth.type'
import { Bet as RiskBet } from '../types/bet.type'
import { BookmakerMarket } from '../types/bookmaker.type'
import { BookmakerEventDetailGroupRowRunner } from '../types/bookmaker-market-detail-group.type'
import { FancyMarket } from '../types/fancy.type'
import { Market, MarketProvider, Side } from '../types/market.type'
import { MarketUpdate } from '../types/websocket-response.type'
import { calculateTotalExposureForBets } from '../utils/exposure.util'
import { BetService } from './bet.service'
import { ExposureService } from './exposure.service'
import { RiskExposureService } from './risk-exposure.service'

@Injectable({
	providedIn: 'root',
})
export class HelperService {
	private readonly toastController = inject(ToastController)

	userDataChanged$ = new BehaviorSubject<UserData | null>(null)

	readonly RATE_MULTIPLIER = 13

	/**
	 * Change texts like MATCH_ODDS to something like Match Odds
	 * @param text
	 */
	getFormalText(text?: string) {
		if (!text) return ''

		return text
			.toLowerCase() // Convert the string to lowercase
			.replace(/_/g, ' ') // Replace underscores with spaces
			.replace(/\b\w/g, (char) => char.toUpperCase()) // Capitalize the first letter of each word
	}

	/**
	 * Get the app value stored in the local storage
	 */
	getUserData(): UserData | null {
		const app = sessionStorage.getItem('app')

		if (app) {
			return JSON.parse(app) as UserData
		}

		return null
	}

	/**
	 * Set the 'app' value stored in the local storage
	 */
	setUserData(data: UserData) {
		sessionStorage.setItem('app', JSON.stringify(data))
		this.userDataChanged$.next(data)
	}

	patchUserFields(patch: Partial<UserData['user_fields']>): boolean {
		const current = this.getUserData()
		if (!current) return false
		this.setUserData({ ...current, user_fields: { ...current.user_fields, ...patch } })
		return true
	}

	/**
	 * Erase the 'app' value stored in the local storage
	 */
	clearUserData() {
		sessionStorage.removeItem('app')
	}

	setRate(rate: string) {
		sessionStorage.setItem('rate', rate)
	}

	getRate(): number {
		return Number(sessionStorage.getItem('rate') ?? '1') * this.RATE_MULTIPLIER
	}

	setWalletId(walletId: string) {
		sessionStorage.setItem('walletId', walletId)
	}

	getWalletId() {
		return sessionStorage.getItem('walletId')
	}

	setSelectedLanguage(selectedLanguage: string) {
		localStorage.setItem('language', selectedLanguage)
	}

	getSelectedLanguage() {
		return localStorage.getItem('language') ?? 'en'
	}

	get locale() {
		const selectedLanguage = this.getSelectedLanguage()
		return SUPPORTED_LOCALES.includes(selectedLanguage) ? selectedLanguage : 'en'
	}

	setTranslateCookie(code: string) {
		const cookieValue = `/en/${code}`
		const isSecure = window.location.protocol === 'https:'
		const attributes = `; path=/; SameSite=Lax${isSecure ? '; Secure' : ''}`
		document.cookie = `googtrans=${cookieValue}${attributes}`
	}

	clearTranslateCookie() {
		const isSecure = window.location.protocol === 'https:'
		const attributes = `; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; SameSite=Lax${isSecure ? '; Secure' : ''}`

		// 1. Clear on current domain
		document.cookie = `googtrans=${attributes}`

		// 2. Clear with explicit hostname
		document.cookie = `googtrans=${attributes}; domain=${window.location.hostname}`

		// 3. Clear with leading dot
		document.cookie = `googtrans=${attributes}; domain=.${window.location.hostname}`

		// 4. Try clearing on parent domain if applicable
		const hostParts = window.location.hostname.split('.')
		if (hostParts.length >= 2) {
			const parentDomain = hostParts.slice(-2).join('.')
			document.cookie = `googtrans=${attributes}; domain=.${parentDomain}`
			document.cookie = `googtrans=${attributes}; domain=${parentDomain}`
		}
	}

	getTranslateCookie(): string {
		const name = 'googtrans='
		const decodedCookie = decodeURIComponent(document.cookie)
		const ca = decodedCookie.split(';')
		for (let c of ca) {
			while (c.charAt(0) === ' ') {
				c = c.substring(1)
			}
			if (c.indexOf(name) === 0) {
				return c.substring(name.length, c.length)
			}
		}
		return ''
	}

	setFavoriteMarkets(keys: string[]) {
		localStorage.setItem('favorites', JSON.stringify(Array.from(new Set(keys))))
	}

	getFavoriteMarkets() {
		return JSON.parse(localStorage.getItem('favorites') ?? '[]') as string[]
	}

	getWhiteLabelBetslipSettings(): BetSlipSetting {
		const userData = this.getUserData()

		return {
			betSlipSettings: {
				appearance: 'traditional',
				cashOut: 'enable',
				defaultStake: '',
				flashOnPriceChange: 'enable',
				whatIfFigure: 'enable',
				persistenceType: 'PERSIST',
			},
			quickBetAmounts: ['5', '10', '20', '50', '100', '200', '500'],
			...userData?.user_fields?.WHITE_LABEL_BET_SLIP,
		}
	}

	getUserBetslipSettings(): BetSlipSetting {
		const userData = this.getUserData()

		if (userData) {
			return {
				...this.getWhiteLabelBetslipSettings(),
				...{
					...this.getWhiteLabelBetslipSettings(),
					...userData.user_fields.USER_BETSLIP,
					betSlipSettings: {
						...this.getWhiteLabelBetslipSettings().betSlipSettings,
						...userData.user_fields.USER_BETSLIP?.betSlipSettings,
						persistenceType: userData.user_fields.USER_BETSLIP?.betSlipSettings?.persistenceType ?? 'LAPSE',
					},
				},
			}
		}

		return this.getWhiteLabelBetslipSettings()
	}

	/**
	 * Return the numerical value of a short number representation
	 * @param value
	 */
	getNumericValue(value: string | number): number {
		const regex = /^([\d.]+)([KMBTkmbt])?$/i
		const match = regex.exec(value.toString())

		if (!match) {
			if (!isNaN(+value)) {
				// If the input is already a number, return it as-is
				return +value
			} else {
				throw new Error(`Invalid input format: ${value}`)
			}
		}

		const numericValue = parseFloat(match[1])
		const suffix = match[2]?.toUpperCase()

		if (!suffix) {
			// No suffix means it's a plain number
			return numericValue
		}

		const chosenSuffix = SHORT_NUMBER_SUFFIXES.find((record) => record.suffix === suffix)
		if (!chosenSuffix) {
			throw new Error(`Unknown suffix: ${suffix}`)
		}

		return numericValue * chosenSuffix.value
	}

	/**
	 * Return a short number representation of a numerical value
	 * @param value
	 */
	getShortNumber(value: number): string {
		for (const { suffix, value: divisor } of SHORT_NUMBER_SUFFIXES) {
			if (value >= divisor) {
				const shortValue = (value / divisor).toFixed(1).replace(/\.0$/, '') // Remove trailing .0
				return `${shortValue}${suffix}`
			}
		}

		// If no suffix is applicable, return the value as is
		return value.toString()
	}

	getDateRange(dateRange: PROFIT_AND_LOSS_DATE_RANGES, config: { utc: boolean } = { utc: true }) {
		const now = constructNow(new Date())
		const todayEnd = endOfDay(now)
		const todayStart = startOfDay(now)
		let fromDate, toDate

		switch (dateRange) {
			case PROFIT_AND_LOSS_DATE_RANGES.TODAY:
				fromDate = todayStart
				toDate = todayEnd
				break
			case PROFIT_AND_LOSS_DATE_RANGES.YESTERDAY:
				fromDate = startOfYesterday()
				toDate = todayEnd
				break
			case PROFIT_AND_LOSS_DATE_RANGES.SEVEN_DAYS:
				fromDate = startOfDay(subDays(now, 6))
				toDate = todayEnd
				break
			case PROFIT_AND_LOSS_DATE_RANGES.FIFTEEN_DAYS:
				fromDate = startOfDay(subDays(now, 14))
				toDate = todayEnd
				break
			case PROFIT_AND_LOSS_DATE_RANGES.THREE_MONTHS:
				fromDate = subMonths(now, 3)
				toDate = todayEnd
				break
			case PROFIT_AND_LOSS_DATE_RANGES.DATE_RANGE:
				toDate = now
				fromDate = subDays(now, 6)
				break
			default:
				throw new Error('Invalid date range')
		}

		if (!config?.utc) {
			return { from: format(fromDate, 'dd-MMM-yyyy'), to: format(toDate, 'dd-MMM-yyyy') }
		}

		return {
			from: fromDate.toISOString(),
			to: toDate.toISOString(),
		}
	}

	getScrollValue(rightEnd: number, nextScrollCoordinate: number, direction: 'LEFT' | 'RIGHT') {
		const scrollValue = nextScrollCoordinate

		if (direction === 'RIGHT') {
			if (scrollValue + TABLE_SCROLL_CONSTANT <= rightEnd) {
				return scrollValue + TABLE_SCROLL_CONSTANT
			} else {
				return rightEnd
			}
		} else {
			if (scrollValue - TABLE_SCROLL_CONSTANT >= 0) {
				return scrollValue - TABLE_SCROLL_CONSTANT
			} else {
				return 0
			}
		}
	}

	/**
	 * Get the username of the logged-in user from local storage
	 */
	getUsername(): string {
		return this.getUserData()?.user_fields?.NAME ?? '-'
	}

	/**
	 * Calculate the amount of odd to increment
	 */
	getOddsIncrement(odd: number) {
		let amount = 0.01

		if (odd >= 2 && odd < 3) {
			amount = 0.02
		} else if (odd >= 3 && odd < 4) {
			amount = 0.05
		} else if (odd >= 4 && odd < 6) {
			amount = 0.1
		} else if (odd >= 6 && odd < 10) {
			amount = 0.2
		} else if (odd >= 10 && odd < 20) {
			amount = 0.5
		} else if (odd >= 20 && odd < 30) {
			amount = 1
		} else if (odd >= 30 && odd < 50) {
			amount = 2
		} else if (odd >= 50 && odd < 100) {
			amount = 5
		} else if (odd >= 100) {
			amount = 10
		}

		return amount
	}

	/**
	 * Calculate the amount of odd to decrement
	 */
	getOddsDecrement(odd: number) {
		let amount = 0.01

		if (odd > 2 && odd <= 3) {
			amount = 0.02
		} else if (odd > 3 && odd <= 4) {
			amount = 0.05
		} else if (odd > 4 && odd <= 6) {
			amount = 0.1
		} else if (odd > 6 && odd <= 10) {
			amount = 0.2
		} else if (odd > 10 && odd <= 20) {
			amount = 0.5
		} else if (odd > 20 && odd <= 30) {
			amount = 1
		} else if (odd > 30 && odd <= 50) {
			amount = 2
		} else if (odd > 50 && odd <= 100) {
			amount = 5
		} else if (odd > 100) {
			amount = 10
		}

		return amount
	}

	/**
	 * Check if the market is a line market or not
	 */
	isOddsMarket(market: Market): boolean {
		return market.description?.['bettingType'] && new RegExp(/ODDS/, 'ig').test(market.description['bettingType'])
	}

	/**
	 * Check if the market is a line market or not
	 */
	isLineMarket(market: Market): boolean {
		return (
			market.description?.['bettingType'] &&
			new RegExp(/LINE/, 'ig').test(market.description['bettingType']) &&
			!this.isAsianHandicapMarket(market) &&
			!this.isGoalLinesMarket(market)
		)
	}

	/**
	 * Determines if the provided market is related to soccer.
	 *
	 * @param {Market} market - The market object to evaluate.
	 * @return {boolean} Returns true if the market is a soccer market; otherwise, false.
	 */
	isSoccerMarket(market: Market): boolean {
		return market.eventType.id.toString() === HOME_SPORT_GROUPS.SOCCER.toString()
	}

	/**
	 * Determines if the provided market is related to horse racing.
	 *
	 * @param {Market} market - The market object to evaluate.
	 * @return {boolean} Returns true if the market is a horse racing market; otherwise, false.
	 */
	isHorseRacingMarket(market: Market): boolean {
		return market.eventType.id.toString() === HOME_SPORT_GROUPS.HORSE_RACING.toString()
	}

	/**
	 * Determines if the provided market is related to greyhound racing.
	 *
	 * @param {Market} market - The market object to evaluate.
	 * @return {boolean} Returns true if the market is a greyhound racing market; otherwise, false.
	 */
	isGreyhoundRacingMarket(market: Market): boolean {
		return market.eventType.id.toString() === HOME_SPORT_GROUPS.GREYHOUND_RACING.toString()
	}

	/**
	 * Checks if the given market is categorized as an Asian Handicap betting type.
	 *
	 * @param {Market} market - The market object to evaluate, containing descriptions and properties related to the betting market.
	 * @return {boolean} True if the market's betting type matches "ASIAN_HANDICAP" and the market name is "Asian Handicap", false otherwise.
	 */
	isAsianHandicapMarket(market: Market): boolean {
		return (
			market.description?.['bettingType'] &&
			new RegExp(/ASIAN_HANDICAP/, 'ig').test(market.description['bettingType']) &&
			market.marketName === 'Asian Handicap'
		)
	}

	/**
	 * Checks if the given market is categorized as a Goal Lines betting type.
	 *
	 * @param {Market} market - The market object to evaluate, containing descriptions and properties related to the betting market.
	 * @return {boolean} True if the market's betting type matches "ASIAN_HANDICAP" and the market name is "Goal Lines", false otherwise.
	 */
	isGoalLinesMarket(market: Market): boolean {
		return (
			market.description?.['bettingType'] &&
			new RegExp(/ASIAN_HANDICAP/, 'ig').test(market.description['bettingType']) &&
			market.marketName === 'Goal Lines'
		)
	}

	/**
	 * Check if the given market is a double-chance market or not.
	 *
	 *
	 * @param {Market} market - The market object to evaluate.
	 * @return {boolean} True if the market is a double-chance
	 */
	isDoubleChance(market: Market): boolean {
		return market.marketName === 'Double Chance'
	}

	/**
	 * Determines whether the given market is a goal market or not.
	 *
	 * @param {Market} market - The market to check, containing a description that may include a bettingType.
	 * @return {boolean} Returns true if the market's bettingType matches "ASIAN_HANDICAP", otherwise false.
	 */
	isGoalMarket(market: Market): boolean {
		return !this.isAsianHandicapMarket(market) && !this.isGoalLinesMarket(market) && market.marketName.includes('Goal')
	}

	/**
	 * Determines if the provided market is the main market.
	 *
	 * @param {Market} market - The market object to be checked.
	 * @return {boolean} True if the market is the main market ('Match Odds'), otherwise false.
	 */
	isMainMarket(market: Market): boolean {
		return market.marketName === 'Match Odds'
	}

	/**
	 * Determines if the provided market object is of type `Market`.
	 *
	 * @param {Market | BookmakerMarket | FancyMarket} market - The market object to be checked.
	 * @return {boolean} True if the object is of type Market; otherwise, false.
	 */
	marketIsBfType(market: Market | BookmakerMarket | FancyMarket): market is Market {
		return (market as Market).marketId.startsWith('1.') !== undefined && (this.isOddsMarket(market as Market) || this.isLineMarket(market as Market))
	}

	/**
	 * Determines if the provided market object is of type `Market`.
	 *
	 * @param {Market | BookmakerMarket | FancyMarket} market - The market object to be checked.
	 * @return {boolean} True if the object is of type Market; otherwise, false.
	 */
	marketIsAutoBookmakerType(market: Market | BookmakerMarket | FancyMarket): market is BookmakerMarket {
		return this.marketIsBookmakerType(market) && (market as BookmakerMarket).name === '0% Commission'
	}

	/**
	 * Determines if the provided market market is of type `BookmakerMarket`.
	 *
	 * @param {Market | BookmakerMarket | FancyMarket} market - The market object to be checked.
	 * @return {boolean} True if the provided market is of type `BookmakerMarket`, false otherwise.
	 */
	marketIsBookmakerType(market: Market | BookmakerMarket | FancyMarket): market is BookmakerMarket {
		return (market as BookmakerMarket).descriptionPrefix === 'Bookmaker'
	}

	/**
	 * Determines if the provided market market is of type `FancyMarket`.
	 *
	 * @param {Market | BookmakerMarket | FancyMarket} market - The market object to be checked.
	 * @return {boolean} Returns true if the market is of type FancyMarket; otherwise, returns false.
	 */
	marketIsFancyType(market: Market | BookmakerMarket | FancyMarket): market is FancyMarket {
		return (market as FancyMarket).descriptionPrefix === 'Fancy'
	}

	/**
	 * Get the F_ODDS value
	 * @param odd
	 * @param provider
	 * @param isLineMarket
	 */
	getFinalOdds(odd: number, provider: MarketProvider, isLineMarket: boolean) {
		if (provider === MarketProvider.BF && isLineMarket) {
			return 2
		}
		if (provider === MarketProvider.SB) {
			return odd / 100 + 1
		}

		return odd
	}

	generateTopic(marketId: string, serviceId: string, providerId: string) {
		const splitServiceId = serviceId.split('-')
		return `sb_${marketId}_${splitServiceId[0]}_${splitServiceId[1]}_${providerId}`
	}

	generateIdsFromTopic(topic: string) {
		const splitTopic = topic.split('_')

		return {
			marketId: splitTopic[1],
			serviceId: `${splitTopic[2]}-${splitTopic[3]}`,
			providerId: splitTopic[4],
		}
	}

	getSportGroupName(sportName: string, provider: string) {
		if (provider === 'FA') {
			return `${sportName} Fancy`
		} else if (provider === 'BM') {
			return `${sportName} Bookmaker`
		}

		return sportName
	}

	generateMarketGroupIdForCalculationBet({
		marketId,
		selectionId,
		provider,
		bettingType,
		topic,
	}: Pick<Bet, 'marketId' | 'provider' | 'bettingType' | 'selectionId' | 'topic'>) {
		if (provider === MarketProvider.BF && bettingType !== 'LINE') {
			// ODDS market
			return marketId
		} else if (provider === MarketProvider.SB && bettingType !== 'LINE') {
			// ODDS market
			return topic ?? `${marketId}-${selectionId}`
		} else {
			// BF LINE, Fancy & Bookmaker market
			return `${marketId}-${selectionId}`
		}
	}

	/**
	 * Get odd value for bookmaker based markets
	 * @param runner
	 * @param side
	 * @param level
	 */
	getOdd(runner: BookmakerEventDetailGroupRowRunner, side: Side, level: 'PRIMARY' | 'SECONDARY' | 'TERTIARY') {
		switch (level) {
			case 'PRIMARY':
				return side === 'BACK'
					? Array.isArray(runner.backOdd)
						? runner.backOdd[0] && runner.backOdd[0] > 0
							? runner.backOdd[0]
							: '-'
						: runner.backOdd && runner.backOdd > 0
							? runner.backOdd
							: '-'
					: Array.isArray(runner.layOdd)
						? runner.layOdd[0] && runner.layOdd[0] > 0
							? runner.layOdd[0]
							: '-'
						: runner.layOdd && runner.layOdd > 0
							? runner.layOdd
							: '-'
			case 'SECONDARY':
				return side === 'BACK'
					? Array.isArray(runner.backOdd)
						? runner.backOdd[1] && runner.backOdd[1] > 0
							? runner.backOdd[1]
							: '-'
						: runner.backOdd && runner.backOdd > 0
							? runner.backOdd
							: '-'
					: Array.isArray(runner.layOdd)
						? runner.layOdd[1] && runner.layOdd[1] > 0
							? runner.layOdd[1]
							: '-'
						: runner.layOdd && runner.layOdd > 0
							? runner.layOdd
							: '-'
			case 'TERTIARY':
				return side === 'BACK'
					? Array.isArray(runner.backOdd)
						? runner.backOdd[2] && runner.backOdd[2] > 0
							? runner.backOdd[2]
							: '-'
						: runner.backOdd && runner.backOdd > 0
							? runner.backOdd
							: '-'
					: Array.isArray(runner.layOdd)
						? runner.layOdd[2] && runner.layOdd[2] > 0
							? runner.layOdd[2]
							: '-'
						: runner.layOdd && runner.layOdd > 0
							? runner.layOdd
							: '-'
		}
	}

	/**
	 * Get size for bookmaker-based markets
	 * @param runner
	 * @param side
	 * @param level
	 */
	getSize(runner: BookmakerEventDetailGroupRowRunner, side: Side, level: 'PRIMARY' | 'SECONDARY' | 'TERTIARY') {
		switch (level) {
			case 'PRIMARY':
				return side === 'BACK'
					? Array.isArray(runner.backSize)
						? runner.backSize[0] && runner.backSize[0] > 0
							? runner.backSize[0]
							: ''
						: runner.backSize && runner.backSize > 0
							? runner.backSize
							: ''
					: Array.isArray(runner.laySize)
						? runner.laySize[0] && runner.laySize[0] > 0
							? runner.laySize[0]
							: ''
						: runner.laySize && runner.laySize > 0
							? runner.laySize
							: ''
			case 'SECONDARY':
				return side === 'BACK'
					? Array.isArray(runner.backSize)
						? runner.backSize[1] && runner.backSize[1] > 0
							? runner.backSize[1]
							: ''
						: runner.backSize && runner.backSize > 0
							? runner.backSize
							: ''
					: Array.isArray(runner.laySize)
						? runner.laySize[1] && runner.laySize[1] > 0
							? runner.laySize[1]
							: ''
						: runner.laySize && runner.laySize > 0
							? runner.laySize
							: ''
			case 'TERTIARY':
				return side === 'BACK'
					? Array.isArray(runner.backSize)
						? runner.backSize[2] && runner.backSize[2] > 0
							? runner.backSize[2]
							: ''
						: runner.backSize && runner.backSize > 0
							? runner.backSize
							: ''
					: Array.isArray(runner.laySize)
						? runner.laySize[2] && runner.laySize[2] > 0
							? runner.laySize[2]
							: ''
						: runner.laySize && runner.laySize > 0
							? runner.laySize
							: ''
		}
	}

	constructOddsAndLineTitle(market: Market) {
		return `${market.event?.name ?? market.eventName ?? ''} - ${market.marketName}`
	}

	constructAutoBookmakerTitle(market: BookmakerMarket, withoutEventName = false) {
		return withoutEventName
			? `${this.getFormalText(market.type)} (0% Commission)`
			: `${market.eventName} - ${this.getFormalText(market.type)} (0% Commission)`
	}

	constructBookmakerTitle(market: BookmakerMarket) {
		return market.type === 'MINI_BOOKMAKER' ? `Mini Bookmaker - ${market.name}` : `${market.descriptionPrefix} - ${market.type}`
	}

	constructFancyTitle(market: FancyMarket) {
		return `Fancy - ${market.eventName}`
	}

	constructSbDetailRequestingKey(descriptionPrefix: string, eventId: string, providerId: string, selectionId?: string | number) {
		return `${descriptionPrefix}_${eventId}_${providerId}|${selectionId}`
	}

	async downloadTableAsExcel(tableId: string, fileName: string) {
		const tableElement = document.getElementById(tableId) as HTMLTableElement

		if (!tableElement) {
			console.error('Table element not found')
			return
		}

		// Notify user of the download start
		const toast = await this.toastController.create({
			message: 'Exporting Excel file...',
			duration: 2000,
			position: 'top',
			color: 'primary',
		})
		await toast.present()

		const ExcelJS = (await import('exceljs')).default
		const workbook = new ExcelJS.Workbook()
		const worksheet = workbook.addWorksheet('Sheet 1')

		const rows: string[][] = []
		for (const row of Array.from(tableElement.rows)) {
			const rowData: string[] = []
			for (const cell of Array.from(row.cells)) {
				rowData.push(cell.textContent?.trim() || '')
			}
			rows.push(rowData)
		}

		rows.forEach((row) => {
			worksheet.addRow(row)
		})

		const buffer = await workbook.xlsx.writeBuffer()
		const finalFileName = `${fileName}.xlsx`

		if (Capacitor.isNativePlatform()) {
			try {
				// Convert ArrayBuffer to Base64 using a pure browser API compatible method
				const bytes = new Uint8Array(buffer)
				let binary = ''
				for (let i = 0; i < bytes.byteLength; i++) {
					binary += String.fromCharCode(bytes[i])
				}
				const base64Data = window.btoa(binary)

				// Save file to native Documents folder
				const savedFile = await Filesystem.writeFile({
					path: finalFileName,
					data: base64Data,
					directory: Directory.Documents,
				})

				try {
					// Attempt to open the file directly (prompts user to view/save in Excel or Google Sheets)
					await FileOpener.openFile({
						path: savedFile.uri,
						mimeType: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
					})
				} catch (e) {
					console.error('Error opening Excel file natively:', e)
					// Fallback: If no app can open xlsx directly, pop the share sheet
					await Share.share({
						title: finalFileName,
						text: `Exported ${finalFileName}`,
						url: savedFile.uri,
						dialogTitle: 'Save or Share Excel File',
					})
				}
			} catch (error) {
				console.error('Error saving or sharing Excel file natively:', error)
			}
		} else {
			const fileSaver = await import('file-saver')
			const saveAs = (fileSaver as unknown as { default: typeof fileSaver.saveAs }).default || fileSaver.saveAs || fileSaver
			// Standard web fallback
			saveAs(new Blob([buffer]), finalFileName)
		}
	}

	isAsianHandicapBet(bet: Pick<Partial<Bet>, 'bettingType' | 'marketName'>) {
		return bet.bettingType && new RegExp(/ASIAN_HANDICAP/, 'ig').test(bet.bettingType) && bet.marketName && bet.marketName === 'Asian Handicap'
	}

	isGoalLinesBet(bet: Pick<Partial<Bet>, 'bettingType' | 'marketName'>) {
		return bet.bettingType && new RegExp(/ASIAN_HANDICAP/, 'ig').test(bet.bettingType) && bet.marketName && bet.marketName === 'Goal Lines'
	}

	isLineBet(bet: Pick<Partial<Bet>, 'bettingType'>) {
		return bet.bettingType === 'LINE'
	}

	isDoubleChanceBet(bet: Pick<Partial<Bet>, 'marketName'>) {
		return bet.marketName === 'Double Chance'
	}

	/**
	 * Determines if the provided update object is of type `MarketUpdate`.
	 *
	 * @param {MarketUpdate | BookmakerMarket | FancyMarket} update - The market update object to be checked.
	 * @return {boolean} True if the object is of type MarketUpdate; otherwise, false.
	 */
	marketUpdateIsBfType(update: MarketUpdate | BookmakerMarket | FancyMarket): update is MarketUpdate {
		return (update as MarketUpdate).r !== undefined
	}

	/**
	 * Determines if the provided market update is of type `BookmakerMarket`.
	 *
	 * @param {MarketUpdate | BookmakerMarket | FancyMarket} update - The market update object to be checked.
	 * @return {boolean} True if the provided update is of type `BookmakerMarket`, false otherwise.
	 */
	marketUpdateIsBookmakerType(update: MarketUpdate | BookmakerMarket | FancyMarket): update is BookmakerMarket {
		return (update as BookmakerMarket).descriptionPrefix === 'Bookmaker'
	}

	/**
	 * Determines if the provided market update is of type `FancyMarket`.
	 *
	 * @param {MarketUpdate | BookmakerMarket | FancyMarket} update - The market update object to be checked.
	 * @return {boolean} Returns true if the update is of type FancyMarket; otherwise, returns false.
	 */
	marketUpdateIsFancyType(update: MarketUpdate | BookmakerMarket | FancyMarket): update is FancyMarket {
		return (update as FancyMarket).descriptionPrefix === 'Fancy'
	}

	/**
	 * Calculate frontend exposure from existing bets (excludes predicted bets)
	 */
	calculateFEExposure(betService: BetService, riskExposureService: RiskExposureService, exposureService: ExposureService): number {
		try {
			const nonPendingBets = betService.nonPendingBets$.getValue().map((bet) => ({
				...bet,
				betSize: bet.stakeRemaining === 0 ? (bet.stakeMatched ?? bet.betSize) : bet.betSize,
				marketGroupId: this.generateMarketGroupIdForCalculationBet(bet),
				dfOdds: bet.provider === MarketProvider.BF && bet.bettingType === 'LINE' ? bet.averagePriceMatched : bet.dfOdds,
				rangeOdd: bet.averagePriceMatched ?? bet.dfOdds,
				sizeMatched: bet.sizeMatched,
			}))
			const allBets = nonPendingBets.filter((bet) => bet.betId !== '-1')

			const matchedOddBets = allBets.filter((bet) => {
				return !this.isDoubleChanceBet(bet) && !this.isAsianHandicapBet(bet) && !this.isGoalLinesBet(bet) && !this.isLineBet(bet)
			})

			if (matchedOddBets.length === 0) return 0

			const betDtos = matchedOddBets.map((bet) => ({
				betId: bet.betId,
				marketId: bet.marketId,
				selectionId: bet.selectionId.toString(),
				side: bet.side.toLowerCase() as 'back' | 'lay',
				price: Number(bet.averagePriceMatched) || Number(bet.fOdds) || 1.01,
				betSize: Number(bet.betSize),
				stakeMatched: Number(bet.stakeMatched),
			}))

			const betModels = betDtos.map((betDto) => RiskBet.fromDto(betDto))
			const marketSelectionIds: Map<string, string[]> = betService.getMarketSelectionIds(betModels)
			const oddsExcludeTotalExposure = exposureService ? calculateTotalExposureForBets(allBets, this, exposureService) : 0

			return -riskExposureService.calculateSumOfIndividualMarketExposures(betModels, marketSelectionIds) - oddsExcludeTotalExposure
		} catch (error) {
			console.error('❌ FE exposure calculation error:', error)
			return 0
		}
	}
}
